import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
  useLayoutEffect,
  useEffect
} from 'react'
import { useDispatch } from 'react-redux'
import moment, { Moment } from 'moment'
import useRouter from '../../../hooks/useRouter'
import { getCompletedYear } from '../../../utils/moment'
import { getARPaymentStatusData, getAPPaymentStatusData } from '../Admin/api'
import {
  getAdminARTableData,
  getAdminAPTableData
} from '../Admin/AdminTable/api'
import { getReportsData } from '../utils'
import { setLoading } from '../../../redux/store/common/slice'
import { DEFAULT_PAGE } from '../../../components/Table/constants'
import { timelineYearFormat } from '../../../components/Timelines/constants'
import { adminTabs } from '../Admin/constants'
import { routes } from '../../../router'
import { FINANCIAL_TABS } from '../constants'
import {
  CognusAdminData,
  CognusAdminDataFilters,
  CognusAdminFilterFunc,
  CognusAdminRequestData,
  CognusAdminTableParams
} from '../Admin/AdminTable/types'
import { PaymentStatus } from '../types'
import { TPageInfo } from '../../../components/Table/types'

type ContextProps = {
  state: {
    totalNumber: number
    totalAmount: number
    firstLoad: boolean
    activeTab: string | null
    pageInfo: TPageInfo
    period: Moment
    tableData: CognusAdminData[]
    reports: PaymentStatus[]
    filters: CognusAdminDataFilters
    sortParams?: CognusAdminRequestData
  }
  actions: {
    setPeriod: Dispatch<SetStateAction<Moment>>
    setPageInfo: Dispatch<SetStateAction<TPageInfo>>
    applyFilters: CognusAdminFilterFunc
    getTableData: (args: CognusAdminRequestData) => Promise<void>
    getChartsData: () => Promise<void>
    updateCognusAdminData: () => Promise<void>
    onChangeTab: (e) => void
  }
}

const FinancialsAdminContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const FinancialsAdminProvider: FC<PropsWithChildren> = ({ children }) => {
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const [totalAmount, setTotalAmount] = useState(0)
  const [totalNumber, setTotalNumber] = useState(0)
  const [firstLoad, setFirstLoad] = useState(true)
  const [period, setPeriod] = useState<Moment>(getCompletedYear())
  const [sortParams, setSortParams] = useState<CognusAdminRequestData>()
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [tableData, setTableData] = useState<CognusAdminData[]>([])
  const [reports, setReports] = useState<PaymentStatus[]>([])
  const [filters, setFilters] = useState<CognusAdminDataFilters>({})
  const dispatch = useDispatch()

  const { query, navigate } = useRouter()
  const isPayable = query.isPayable

  useLayoutEffect(() => {
    if (isPayable && !activeTab) {
      setActiveTab(adminTabs.payable)
    } else if (!isPayable && !activeTab) {
      setActiveTab(adminTabs.receivable)
    }
  }, [isPayable, activeTab])

  useEffect(() => {
    if (isPayable) {
      navigate(`${routes.financials}/${FINANCIAL_TABS[1].key}`, {
        replace: true
      })
    }
    // eslint-disable-next-line
  }, [isPayable])

  const getTableData = useCallback(
    async ({ sortField, sortOrder, filters, page }: CognusAdminRequestData) => {
      try {
        dispatch(setLoading(true))
        const params: CognusAdminTableParams = {
          ...filters,
          year: moment(period).format(timelineYearFormat)
        }
        if (sortField) {
          if (sortOrder) {
            params.ordering = `${
              sortOrder === 'descend' ? '-' : ''
            }${sortField}`
          }
          setSortParams({
            sortField,
            sortOrder
          })
        }
        const dataPage = page ? page : DEFAULT_PAGE
        params.limit = dataPage.pageSize
        params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
        const getAdminTableData =
          activeTab === adminTabs.receivable
            ? getAdminARTableData
            : getAdminAPTableData
        const { data } = await getAdminTableData({ params })
        setTableData(
          data.results.map((row) => ({
            ...row,
            key: row.uuid
          }))
        )
        setTotalNumber(data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      } finally {
        dispatch(setLoading(false))
      }
    },
    [activeTab, dispatch, firstLoad, period]
  )

  const getChartsData = useCallback(async () => {
    try {
      dispatch(setLoading(true))
      const getPaymentStatusData =
        activeTab === adminTabs.receivable
          ? getARPaymentStatusData
          : getAPPaymentStatusData
      const { data } = await getPaymentStatusData({
        params: {
          year: moment(period).format(timelineYearFormat)
        }
      })
      const results = getReportsData(data.results)
      setReports(results)
      setTotalAmount(results.reduce((prev, cur) => prev + cur.amount, 0))
    } finally {
      dispatch(setLoading(false))
    }
  }, [activeTab, dispatch, period])

  const applyFilters: CognusAdminFilterFunc = useCallback(
    (field, appliedFilters) => {
      const newFilters = { ...filters, [field]: appliedFilters }
      const newPageInfo = { ...pageInfo, pageNumber: 1 }
      setFilters(newFilters)
      setPageInfo(newPageInfo)
      void getTableData({
        ...sortParams,
        filters: newFilters,
        page: newPageInfo
      })
    },
    [filters, getTableData, pageInfo, sortParams]
  )

  const updateCognusAdminData = useCallback(async () => {
    await getChartsData()
    await getTableData({ ...sortParams, page: pageInfo })
  }, [getChartsData, getTableData, pageInfo, sortParams])

  const onChangeTab = useCallback(async (e: string) => setActiveTab(e), [])

  const context = useMemo(
    () => ({
      state: {
        totalNumber,
        tableData,
        firstLoad,
        totalAmount,
        reports,
        period,
        pageInfo,
        sortParams,
        activeTab,
        filters
      },
      actions: {
        setPeriod,
        setPageInfo,
        getTableData,
        getChartsData,
        updateCognusAdminData,
        onChangeTab,
        applyFilters
      }
    }),
    [
      totalNumber,
      tableData,
      firstLoad,
      totalAmount,
      reports,
      period,
      pageInfo,
      sortParams,
      activeTab,
      filters,
      getTableData,
      getChartsData,
      updateCognusAdminData,
      onChangeTab,
      applyFilters
    ]
  )

  return (
    <FinancialsAdminContext.Provider value={context}>
      {children}
    </FinancialsAdminContext.Provider>
  )
}

export const useFinancialsAdminContext = () =>
  useContext(FinancialsAdminContext)

export default FinancialsAdminProvider
